import {
  AuthenticationDetails,
  CognitoIdToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  type ISignUpResult,
} from "amazon-cognito-identity-js";
import {
  createSessionReader,
  makeGatedUser,
  storedIdTokenPayload,
  type SessionRead,
} from "@heraion/shared";
import { webStorage } from "../webStorage";

// Pool config comes from build-time env vars, and RW-27 is the reminder that
// TWO paths supply them, both real:
//   - `infra/web-rnw.ts:50-54` — SST hands them to the StaticSite on deploy.
//     (The old note here pointed at `infra/web.ts`, which is the LEGACY web
//     app's file, not this one.)
//   - `scripts/publish-web.sh:104-111` — the manual publish reads the stage's
//     coordinates from `infra/stages.json` and passes the same five on the
//     build command line, deliberately with no defaults.
// ⛔ What does NOT supply them is a `.env.production`. There is none, and there
// cannot be one: `.gitignore` covers `.env.*`. Anyone reaching for that file to
// "fix" a blank local build is about to commit stage config into the repo.
//
// ⚠️ "Fail loud" holds only with devtools open: this throws at MODULE LOAD,
// before React mounts, so a build made without the vars is a blank root and a
// console line. Consequences and the way round it: serve-local.sh, trap 2.
const userPoolId = import.meta.env["VITE_USER_POOL_ID"];
const clientId = import.meta.env["VITE_USER_POOL_CLIENT_ID"];
if (!userPoolId || !clientId) {
  throw new Error(
    "Cognito env not configured (VITE_USER_POOL_ID / VITE_USER_POOL_CLIENT_ID).",
  );
}

// Token storage:
//   amazon-cognito-identity-js defaults to window.localStorage. That's the
//   standard SPA trade — XSS-readable, but cookies (httpOnly + CSRF) require
//   a backend session layer we don't have and don't need at family scale.
//   Mitigation: strict CSP at the CloudFront layer once we add it, and we
//   rotate refresh tokens (enableTokenRevocation in infra/auth.ts).
//   If we ever want to swap to in-memory + httpOnly cookies, this is the
//   single seam to change. Named here, not left to the SDK default, because
//   the session reader below writes through a gate on this same store.
// Blocked storage falls back to memory (../webStorage.ts).
const storage = webStorage;
const pool = new CognitoUserPool({
  UserPoolId: userPoolId,
  ClientId: clientId,
  Storage: storage,
});

// The same store as the pool's: with the memory way out, the SDK default
// would be a second, separate memory and sign-in would land in neither.
const userFor = (email: string) =>
  new CognitoUser({ Username: email, Pool: pool, Storage: storage });

export type AuthError = { code: string; message: string };

/** Il messaggio da mostrare, da un rigetto di questa modulo. ⛔ `AuthError` e'
 *  un oggetto SEMPLICE, non un `Error`: passarlo grezzo a un componente che fa
 *  `String(err)` stampa "[object Object]" invece del motivo. Sei callsite lo
 *  riscrivevano a mano (RW-49); questa e' quella riga, una volta sola. */
const toAuthError = (err: unknown): AuthError => {
  if (err && typeof err === "object") {
    const e = err as { code?: string; name?: string; message?: string };
    return {
      code: e.code ?? e.name ?? "UnknownError",
      message: e.message ?? "Unknown error",
    };
  }
  return { code: "UnknownError", message: String(err) };
};

export const signUp = (email: string, password: string) =>
  new Promise<ISignUpResult>((resolve, reject) => {
    pool.signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: "email", Value: email })],
      [],
      (err, result) => {
        if (err || !result) return reject(toAuthError(err));
        resolve(result);
      },
    );
  });

export const confirmSignUp = (email: string, code: string) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).confirmRegistration(code, true, (err) => {
      if (err) return reject(toAuthError(err));
      resolve();
    });
  });

export const resendConfirmationCode = (email: string) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).resendConfirmationCode((err) => {
      if (err) return reject(toAuthError(err));
      resolve();
    });
  });

// Triggers Cognito's password-reset flow: a code is sent to the email
// on file. The promise resolves once the code is in flight; the user
// then types the code + a new password into confirmForgotPassword().
// preventUserExistenceErrors is enabled on the pool, so an unknown
// email also resolves successfully — the surface stays opaque.
export const forgotPassword = (email: string) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).forgotPassword({
      onSuccess: () => resolve(),
      inputVerificationCode: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
    });
  });

export const confirmForgotPassword = (
  email: string,
  code: string,
  newPassword: string,
) =>
  new Promise<void>((resolve, reject) => {
    userFor(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(toAuthError(err)),
    });
  });

// Gemella di apps/mobile/src/auth/cognito.ts — vedi lì il perché per esteso.
// In breve: chi entra su invito nasce in `FORCE_CHANGE_PASSWORD` e Cognito
// risponde con la sfida `NEW_PASSWORD_REQUIRED`, che senza il suo callback
// faceva morire l'SDK su `callback.newPasswordRequired is not a function`.
// La sfida non chiede attributi (misurato l'1/09), quindi `complete` prende
// solo la password nuova, e la completa sulla STESSA istanza che l'ha ricevuta.
export type SignInOutcome =
  | { kind: "session"; session: CognitoUserSession }
  | {
      kind: "new_password_required";
      complete: (newPassword: string) => Promise<CognitoUserSession>;
    };

export const signIn = (email: string, password: string) =>
  new Promise<SignInOutcome>((resolve, reject) => {
    const user = userFor(email);
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => {
          sessions.reset();
          resolve({ kind: "session", session });
        },
        onFailure: (err) => reject(toAuthError(err)),
        newPasswordRequired: () =>
          resolve({
            kind: "new_password_required",
            complete: (newPassword: string) =>
              new Promise<CognitoUserSession>((res, rej) => {
                user.completeNewPasswordChallenge(
                  newPassword,
                  {},
                  {
                    onSuccess: (session) => {
                      sessions.reset();
                      res(session);
                    },
                    onFailure: (e) => rej(toAuthError(e)),
                  },
                );
              }),
          }),
      },
    );
  });

export const signOut = () => {
  // First: a refresh still running must not write the tokens back.
  sessions.reset();
  const u = pool.getCurrentUser();
  if (u) u.signOut();
};

// Change the signed-in user's password (Settings, Account). No email
// round-trip: the current session authorises the change. Mirrors the
// mobile cognito.changePassword: the session comes from the reader, and the
// user it is set on writes nothing to storage.
export const changePassword = async (oldPassword: string, newPassword: string) => {
  const session = await sessions.token().catch((err: unknown) => {
    throw toAuthError(err);
  });
  const u = session ? gatedUser(() => false) : null;
  if (!session || !u) throw toAuthError({ code: "NoCurrentUser" });
  u.setSignInUserSession(session);
  await new Promise<void>((resolve, reject) => {
    u.changePassword(oldPassword, newPassword, (e) => {
      if (e) return reject(toAuthError(e));
      resolve();
    });
  });
};

// Twin of the mobile reader (apps/mobile/src/auth/cognito.ts), same shared
// logic (packages/shared/src/sessionReader.ts). One pool, so the epoch is
// only the account: signIn and signOut reset it.
const gatedUser = makeGatedUser(CognitoUser, () => pool, storage);
const sessions = createSessionReader<CognitoUserSession>({
  getCurrentUser: gatedUser,
  epoch: () => clientId,
  refreshSlackSeconds: 60,
});

/** The claims on the last user's stored id token, expired or not, with no
 *  network: who is signed in when the session is unknown at boot. */
export const storedClaims = (): Record<string, unknown> | null =>
  storedIdTokenPayload(
    storage,
    clientId,
    pool.getCurrentUser()?.getUsername() ?? null,
    (jwt) => new CognitoIdToken({ IdToken: jwt }).decodePayload(),
  );

// The offline boot's session arriving late (AuthContext): what could not read
// without a session reads again.
const lateSession = new Set<() => void>();

/** Calls `fn` when the offline boot's session arrives. Returns the unsubscribe. */
export const onLateSession = (fn: () => void): (() => void) => {
  lateSession.add(fn);
  return () => {
    lateSession.delete(fn);
  };
};

export const announceLateSession = (): void => {
  for (const fn of [...lateSession]) fn();
};

/** Boot: session, none (signed out) or unknown (timeout, network). */
export const readCurrentSession = (): Promise<SessionRead<CognitoUserSession>> =>
  sessions.read();

// ID token is what we send to API Gateway (its audience matches the
// client ID, which is the audience configured on the JWT authorizer).
// Null when signed out; rejects with SessionUnknownError when unknown.
export const getIdToken = async (): Promise<string | null> =>
  (await sessions.token())?.getIdToken().getJwtToken() ?? null;
